import { useEffect, useRef, useState } from "react";
import { Pin, X } from "lucide-react";

import { PinnedPanel } from "~/components/PinnedPanel";
import type { PinnedMessagePreview } from "~/hooks/usePinned";
import { ICON_STROKE } from "~/lib/icon";

export interface PinnedBannerProps {
  roomId: string;
  pins: PinnedMessagePreview[];
  canManage: boolean;
  onJump: (eventId: string) => void;
  onUnpinAll: () => Promise<void>;
}

export function PinnedBanner({ roomId, pins, canManage, onJump, onUnpinAll }: PinnedBannerProps) {
  const newest = pins[0] ?? null;
  const [dismissed, setDismissed] = useState<{ roomId: string; eventId: string | null }>(() => ({
    roomId,
    eventId: readDismissed(roomId),
  }));
  const [panelOpen, setPanelOpen] = useState(false);
  const viewAllRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setDismissed({ roomId, eventId: readDismissed(roomId) });
  }, [roomId]);

  if (!newest) return null;
  const dismissedEventId = dismissed.roomId === roomId ? dismissed.eventId : readDismissed(roomId);
  if (dismissedEventId === newest.eventId) return null;

  const preview = truncate(newest.body, 80);

  return (
    <div
      style={{
        height: 32,
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        padding: "0 var(--chat-space-3)",
        background: "var(--chat-surface-panel)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        flexShrink: 0,
      }}
    >
      <Pin
        size={16}
        strokeWidth={ICON_STROKE}
        style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
      />
      <span style={{ color: "var(--chat-fg-primary)", fontWeight: 500 }}>Pinned</span>
      <span
        style={{
          minWidth: 0,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {preview}
      </span>
      <button
        ref={viewAllRef}
        type="button"
        onClick={() => setPanelOpen((open) => !open)}
        aria-haspopup="dialog"
        aria-expanded={panelOpen}
        className="chat-button"
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        View all
      </button>
      <button
        type="button"
        aria-label="Dismiss pinned banner"
        onClick={() => {
          window.localStorage.setItem(dismissedKey(roomId), newest.eventId);
          setDismissed({ roomId, eventId: newest.eventId });
          setPanelOpen(false);
        }}
        className="chat-button"
        style={{
          width: 24,
          height: 24,
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: "transparent",
          color: "var(--chat-fg-muted)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <X size={14} strokeWidth={ICON_STROKE} />
      </button>
      {panelOpen && (
        <PinnedPanel
          anchor={viewAllRef.current}
          pins={pins}
          canManage={canManage}
          onJump={onJump}
          onUnpinAll={onUnpinAll}
          onClose={() => setPanelOpen(false)}
        />
      )}
    </div>
  );
}

function dismissedKey(roomId: string): string {
  return `chat.pinnedBanner.${roomId}.dismissed`;
}

function readDismissed(roomId: string): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(dismissedKey(roomId));
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 3)}...`;
}
